import { ContactButtons } from '@/components/contact-buttons';
import { AttributesCard } from '@/components/profile/attributes';
import { Header } from '@/components/profile/header';
import { PricingCard } from '@/components/profile/pricing';
import { ReviewsCard } from '@/components/profile/reviews';
import { ServicesCard } from '@/components/profile/services';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { type ContactLinks, type Profile, type ServiceItem } from '@/types';
import { Link } from '@inertiajs/react';
import { Clock, ExternalLink, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProfilePreview {
  profile: Profile;
  services: ServiceItem[];
  contactLinks?: ContactLinks;
}

interface ProfileModalProps {
  profileId: string | number | null;
  onClose: () => void;
}

export function ProfileModal({ profileId, onClose }: ProfileModalProps) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<ProfilePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [capped, setCapped] = useState(false);

  useEffect(() => {
    if (!profileId) return;

    setData(null);
    setCapped(false);
    setLoading(true);

    fetch(`/p/${profileId}/preview`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => {
        if (r.status === 403) {
          setCapped(true);
          return null;
        }
        return r.json();
      })
      .then((json) => json && setData(json))
      .finally(() => setLoading(false));
  }, [profileId]);

  const hasContact =
    data?.contactLinks &&
    (data.contactLinks.whatsapp ||
      data.contactLinks.telegram ||
      data.contactLinks.book);

  return (
    <Sheet
      open={profileId !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      {/*
        Sheet is a flex column filling the full height:
          - Close button row  (shrink-0)
          - Scrollable content (flex-1, overflow-y-auto)
          - Sticky CTA footer (shrink-0)

        This avoids `position:fixed` for the footer which breaks on mobile
        when the sheet itself is a stacking context.
      */}
      <SheetContent
        side="right"
        showClose={false}
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        aria-describedby={undefined}
        onInteractOutside={onClose}
      >
        {/* ── Header bar with explicit close button ──────────────── */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <SheetTitle className="text-sm font-semibold text-foreground">
            {data?.profile.display_name ?? ' '}
          </SheetTitle>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && capped && (
            <div className="flex h-full flex-col items-center justify-center gap-4 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Clock className="h-7 w-7 text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold">
                {t('profile.capped_title')}
              </h2>
              <p className="max-w-xs text-sm text-muted-foreground">
                {t('profile.capped_body')}
              </p>
              <Button variant="outline" onClick={onClose}>
                {t('profile.capped_cta')}
              </Button>
            </div>
          )}

          {data && (
            <div className="space-y-4">
              <Header profile={data.profile} />
              <PricingCard rates={data.profile.rates} />
              <ServicesCard services={data.services} />
              <AttributesCard
                attributes={data.profile.attributes}
                languages={data.profile.languages}
              />
              {data.profile.reviews.length > 0 && (
                <ReviewsCard
                  reviews={data.profile.reviews}
                  rating={data.profile.rating}
                />
              )}
            </div>
          )}
        </div>

        {/* ── Sticky CTA footer ───────────────────────────────────── */}
        {data && (
          <div className="shrink-0 space-y-2 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-sm">
            {/* Contact buttons — only shown when available */}
            {hasContact && (
              <ContactButtons
                links={data.contactLinks!}
                name={data.profile.display_name}
                stacked={false}
              />
            )}
            {/* View full profile — always visible */}
            <Button
              variant={hasContact ? 'outline' : 'default'}
              size="action"
              className="w-full"
              asChild
            >
              <Link
                href={data.profile.slug ? `/escort/${data.profile.slug}` : `/p/${profileId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                {t('profile.view_full_profile')}
              </Link>
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
